import type {
  ListingCampaignDetailsDTO,
  ListingCampaignSummaryDTO,
} from "./campaign";
import {
  DateString,
  DocumentFileType,
  JsonValue,
  SystemProvider,
  TimestampString,
} from "./common";
export type { PageResponse } from "./api";

// ==========================================
// NYTT: DTOs (Används för listan, detaljsidan och ansökningar)
// ==========================================

/**
 * How a home is applied for.
 *
 * `QUEUE_REQUIRED` needs the student to stand in the owning company's housing
 * queue; `FIRST_COME` is open to anyone eligible. Private listings are always
 * first come — there is no queue to stand in.
 */
export type ApplicationMode = "QUEUE_REQUIRED" | "FIRST_COME";

// 1. KORTET (Feed)
export interface ListingCardDTO {
  id: string;
  imageUrl: string;
  title: string;
  location: string;
  rent: number;
  dwellingType: string;
  /** Display label for the dwelling type, localized by the backend. */
  dwellingTypeLabel?: string | null;
  rooms: number;
  sizeM2: number;
  tags: ListingTagDTO[];
  hostType: string;
  hostName?: string;
  hostLogoUrl?: string;
  verifiedHost: boolean;
  lat?: number | null;
  lng?: number | null;
  status?: ListingStatus | string | null;
  applyBy?: DateString | null;
  availableFrom?: DateString | null;
  availableTo?: DateString | null;
  /** Swagger field for ListingCardDTO. */
  requirementProfileId?: string | null;
  /** @deprecated Detail DTO uses requirementsProfileId; cards use requirementProfileId. */
  requirementsProfileId?: string | null;
  published?: TimestampString | null;
  nearbyLocations?: ListingNearbyLocationDTO[];
  applicationMode?: ApplicationMode | null;
  /** Localized label for applicationMode, resolved by the backend. */
  applicationModeLabel?: string | null;
  /**
   * Live campaigns reduced to what a card can show: one badge, a count, and the
   * restated price where a campaign restates it. The backend sends the empty
   * shape rather than omitting it, so `campaigns.count` is always safe to read.
   */
  campaigns?: ListingCampaignSummaryDTO | null;
}

// 2. DETALJVYN (Single Listing) - Matchar Java ListingDetailDTO
export interface ListingDetailDTO {
  id: string;
  title: string;
  city: string;
  area: string;
  fullAddress?: string | null;
  rent: number;
  dwellingType: string;
  /** Display label for the dwelling type, localized by the backend. */
  dwellingTypeLabel?: string | null;
  rooms: number;
  sizeM2: number | null;
  /**
   * Floor the home is on: 0 is the entrance floor, -1 a basement level, and
   * null means the source never said. Falsy-checking this hides the ground
   * floor — test against null.
   */
  floor?: number | null;
  description: string;
  tags: ListingTagDTO[];
  imageUrls: string[]; 
  
  // Datum
  moveIn?: DateString | null;
  applyBy?: DateString | null;
  availableFrom?: DateString | null;
  availableTo?: DateString | null;

  // Karta
  lat?: number | null;
  lng?: number | null;

  // Värd / Ägare
  ownerType: string;
  ownerName: string;
  ownerLogoUrl?: string | null;
  ownerId: number;
  provider?: SystemProvider | string | null;
  status?: ListingStatus | string | null;
  verifiedOwner: boolean;
  requirementsProfileId?: string | null;
  published?: TimestampString | null;
  nearbyLocations?: ListingNearbyLocationDTO[];
  applicationMode?: ApplicationMode | null;
  /** Localized label for applicationMode, resolved by the backend. */
  applicationModeLabel?: string | null;
  /**
   * Every campaign running on this listing, in reading order, plus the same
   * price figures the card carries so the two surfaces cannot disagree.
   */
  campaigns?: ListingCampaignDetailsDTO | null;
}

export interface ListingNearbyLocationDTO {
  location: "GYM" | "GROCERIES" | "NIGHTCLUB" | "UNIVERSITY" | "TRANSIT" | string;
  lat?: number | null;
  lng?: number | null;
  details?: string | null;
}

export interface ListingTagDTO {
  tagKey?: string | null;
  displayName: string;
  icon?: string | null;
}

interface RequiredDocument {
  caption?: string | null;
  validityDays?: number | null;
  mandatory?: boolean | null;
  validTypes?: Array<DocumentFileType | string>;
}

export interface RequirementsProfileDTO {
  id?: string | null;
  title?: string | null;
  minAge?: number | null;
  maxAge?: number | null;
  description?: string | null;
  requiredDocuments?: RequiredDocument[];
}

// 3. MINA ANSÖKNINGAR (My Applications) - Matchar Java StudentApplicationDTO
export interface StudentApplicationDTO {
  applicationId: number;
  status: string;        // "submitted", "accepted", etc.
  appliedAt: string;     // ISO-datum (Instant från Java)
  message?: string | null;
  
  // Bostadsinfo för kortet
  listingId: string;
  listingTitle: string;
  listingImage: string;
  rent: number;
  city: string;
  hostType: string;
}

/**
 * An application the student made directly in a company's own portal, on an
 * advert CampusLyan does not carry. Matchar Java ExternalApplicationDTO.
 *
 * Read-only: there is no listing to open and withdrawing it has to happen in
 * the company's portal. Shown anyway because it occupies one of the student's
 * slots under that company's application cap.
 */
export interface ExternalApplicationDTO {
  companyId: number;
  companyName: string;
  /** The advert's title as the company worded it; null if none was reported. */
  title?: string | null;
  status: string;
  /** Whether it still counts against the company's cap. */
  active: boolean;
  appliedAt?: string | null;
}

export const LISTING_STATUS_VALUES = ["AVAILABLE", "HIDDEN", "RENTED"] as const;
export type ListingStatus = (typeof LISTING_STATUS_VALUES)[number];

export const DWELLING_TYPE_VALUES = ["APARTMENT", "ROOM", "CORRIDOR_ROOM"] as const;
export type DwellingType = (typeof DWELLING_TYPE_VALUES)[number];

export const HOST_TYPE_VALUES = ["COMPANY", "PRIVATE"] as const;
export type HostType = (typeof HOST_TYPE_VALUES)[number];

export interface UpdateListingRequest {
  title?: string;
  /** English title; empty string clears the translation (falls back to Swedish). */
  titleEn?: string;
  rooms?: number | null;
  sizeM2?: number | null;
  rent?: number | null;
  description?: string;
  /** English description; empty string clears the translation (falls back to Swedish). */
  descriptionEn?: string;
  /**
   * The area shown for the listing. An empty string restores the area the
   * property system imported, which is kept underneath and never edited in
   * place — so a sync cannot overwrite what the owner wrote here.
   */
  area?: string;
  tags?: string[];
  status?: ListingStatus;
  images?: string[];
  applyBy?: DateString | null;
  availableFrom?: DateString | null;
  availableTo?: DateString | null;
  /**
   * Per-listing override of the company's default. Omitted leaves it unchanged;
   * requiring a queue at a company that runs none is refused by the backend.
   */
  applicationMode?: ApplicationMode;
}

/** Owner edit view of a listing's texts in both languages (GET /listings/{id}/translations). */
export interface ListingTranslationsDTO {
  /** Swedish (base) title. */
  title?: string | null;
  /** Swedish (base) description. */
  description?: string | null;
  /** English title; null when falling back to Swedish. */
  titleEn?: string | null;
  /** English description; null when falling back to Swedish. */
  descriptionEn?: string | null;
  /**
   * The area as displayed: the owner's own name when they wrote one, otherwise
   * the imported one. Never the English variant, so the edit form cannot save a
   * translation back as the base name.
   */
  area?: string | null;
}

export interface UpdateMultipleListingsRequest {
  listingDatas: Record<string, UpdateListingRequest>;
}

export interface PublishListingRequest {
  title?: string;
  city?: string;
  area?: string;
  address?: string;
  dwellingType?: DwellingType | string;
  rooms?: number | null;
  sizeM2?: number | null;
  rent?: number | null;
  description?: string;
  tags?: string[];
  images?: string[];
  applyBy?: DateString | null;
  availableFrom?: DateString | null;
  availableTo?: DateString | null;
}

export interface ApplicationRequest {
  message?: string;
}

export interface Ad {
  id: number;
  start?: DateString | null;
  stop?: DateString | null;
  company?: string | null;
  data?: JsonValue;
}
